import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { queryKeys } from '../queryKeys';
import useAxios from '../../hooks/useAxios';
import { MESSAGES_CONVERSATION_URL } from '../../utils/constant';
import { MessageInterface, MessagePaginationInfo } from '../../interface/Message.interface';
import { Message } from '../../Models/Message';
import { useContext } from 'react';
import { LoginContext } from '../../contexts/LoginContext';

interface MessagesResponse {
  success: boolean;
  messages: Message[];
  pagination: MessagePaginationInfo | null;
}

export function useMessages(conversationId: string | null) {
  const { getWithAxios } = useAxios();
  const { currentUser } = useContext(LoginContext);

  return useQuery({
    queryKey: queryKeys.messages.conversation(conversationId || ''),
    queryFn: async (): Promise<MessagesResponse> => {
      if (!conversationId) {
        return { success: false, messages: [], pagination: null };
      }
      const response = await getWithAxios(`${MESSAGES_CONVERSATION_URL(conversationId)}?page=1&limit=30`);
      const messages = (response?.messages || []).map(
        (msg: MessageInterface) => new Message(msg)
      );
      return {
        success: response?.success ?? false,
        messages,
        pagination: response?.pagination || null,
      };
    },
    enabled: !!conversationId && !!currentUser?.isLogged?.(),
    staleTime: 60 * 1000, // 1 minute for messages
  });
}

export function useInfiniteMessages(conversationId: string | null) {
  const { getWithAxios } = useAxios();
  const { currentUser } = useContext(LoginContext);

  return useInfiniteQuery({
    queryKey: [...queryKeys.messages.conversation(conversationId || ''), 'infinite'],
    queryFn: async ({ pageParam = 1 }): Promise<MessagesResponse> => {
      if (!conversationId) {
        return { success: false, messages: [], pagination: null };
      }
      const response = await getWithAxios(`${MESSAGES_CONVERSATION_URL(conversationId)}?page=${pageParam}&limit=30`);
      const messages = (response?.messages || []).map(
        (msg: MessageInterface) => new Message(msg)
      );
      return {
        success: response?.success ?? false,
        messages,
        pagination: response?.pagination || null,
      };
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination?.hasMore) {
        return (lastPage.pagination.page || 0) + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled: !!conversationId && !!currentUser?.isLogged?.(),
    staleTime: 60 * 1000,
  });
}
